/**
 * Everything that differs between environments, resolved in one place.
 *
 * Two layers, first match wins:
 *
 * 1. **Runtime.** `src/templates/index.html` declares each value as a `window.*`
 *    assignment holding a `__NAME__` placeholder, and
 *    `docker/docker-entrypoint.sh` substitutes the container's environment
 *    variables into it before nginx starts. Nothing environment-specific is
 *    baked into the bundle, so the same image is promoted from dev to prod.
 * 2. **Build-time defaults.** The `BUILD_*` globals from `rspack/build-vars.js`,
 *    fed by `.env` locally (see `.env.example`). They apply whenever a
 *    placeholder was never substituted — always the case for `npm start`.
 *
 * The placeholder pattern is the signal that substitution did not happen, so a
 * literal `'__API_BASE_URL__'` never reaches `fetch`.
 *
 * Consumers import from here. Nothing else in the app reads `window.*` or a
 * `BUILD_*` global.
 */

const PLACEHOLDER = /^__[A-Z_]+__$/;

/** The injected value, or undefined when the placeholder still stands. */
function injected(value: string | undefined): string | undefined {
  return typeof value === 'string' && !PLACEHOLDER.test(value) ? value : undefined;
}

/**
 * Origin of survey-api, serving both `/auth/*` and `/api/v1/*`. An empty string
 * means same-origin — the API is reverse-proxied under this host, so no CORS
 * allowlist entry is needed. Empty is a real answer here, which is why only the
 * placeholder falls through to the default.
 */
export const API_BASE_URL: string = injected(window.API_BASE_URL) ?? BUILD_API_BASE_URL;

/**
 * Whether this deployment mounts the demo (`src/demo`): its guide, its mock screens and their
 * routes. `true` and `false` are the only values, and the entrypoint refuses any other before
 * nginx starts — so what reaches the page is one of the two, or the placeholder. Anything but
 * `true` is off here all the same: a demo shown by a typo would put invented screens in front of
 * somebody who was not told to expect them, and a demo hidden by one costs a restart.
 */
export const DEMO: boolean = (injected(window.DEMO) ?? BUILD_DEMO) === 'true';

/**
 * Invent's IdP, where both portals sign in: its origin, the portal's client
 * (`po-{portalId}-code`) and our tenant, sent in `acr_values` as `tenant:{id}` —
 * without it the IdP's form cannot route an invited guest. The entrypoint refuses
 * to start without any of the three; `src/domains/auth/idp.ts` is their one
 * reader. The redirect URI is not configured: it is this origin's
 * `/auth/callback`, which the portal registers per origin.
 */
export const IDP_AUTHORITY: string = injected(window.IDP_AUTHORITY) ?? BUILD_IDP_AUTHORITY;
export const IDP_CLIENT_ID: string = injected(window.IDP_CLIENT_ID) ?? BUILD_IDP_CLIENT_ID;
export const IDP_TENANT_ID: string = injected(window.IDP_TENANT_ID) ?? BUILD_IDP_TENANT_ID;
